import { cn } from "@/lib/utils";
import {
  InlineError,
  InlineSpinner,
  inputClasses,
  singleLineKeys,
  useInlineEdit,
  ViewSurface,
  type InlineSize,
} from "./shared";

/**
 * A single-line text value, edited in place. The opportunity name is the one this exists
 * for; the client and team-member modals inherit it later.
 *
 * Trimmed before comparison as well as before sending, so re-typing the same value with a
 * stray space is still a no-op rather than a version bump.
 */
export function InlineText({
  label,
  value,
  editable,
  disabled,
  size = "sm",
  maxLength,
  className,
  onCommit,
}: {
  label: string;
  value: string;
  editable: boolean;
  disabled?: boolean;
  size?: InlineSize;
  maxLength?: number;
  className?: string;
  onCommit: (next: string) => Promise<unknown>;
}) {
  const field = useInlineEdit<string>({
    value,
    equals: (a, b) => a.trim() === b.trim(),
    onCommit: (next) => onCommit(next.trim()),
  });

  if (!field.editing) {
    return (
      <ViewSurface
        label={label}
        editable={editable}
        disabled={disabled}
        saving={field.saving}
        size={size}
        onActivate={field.begin}
        className={className}
      >
        {value}
      </ViewSurface>
    );
  }

  return (
    <div className="min-w-0">
      <div className="flex items-center gap-1.5">
        <input
          aria-label={label}
          value={field.draft}
          maxLength={maxLength}
          disabled={field.saving}
          onChange={(event) => field.setDraft(event.target.value)}
          onBlur={() => void field.commit(field.draft)}
          onKeyDown={singleLineKeys(field, () => field.draft)}
          // Inside an already-open modal this only chooses which element has focus, which is
          // the whole point of clicking a value: it is an input, ready to be typed into.
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          className={cn(inputClasses(size), className)}
        />
        {field.saving && <InlineSpinner />}
      </div>
      {field.error && <InlineError message={field.error} />}
    </div>
  );
}
